import { useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

interface UpdateBannerShellProps {
  message: string;
  actionLabel: string;
  onAction: () => void;
  spacerTestId: string;
}

const BANNER_HEIGHT_VARIABLE = "--update-banner-height";

/**
 * Gabarit commun aux deux bandeaux de mise à jour (urgence web, rappel des
 * apps natives). Volontairement SANS bouton de fermeture. Fixe, jamais
 * d'overflow sur body.
 *
 * Un bloc vide placé DANS le flux réserve la hauteur réelle du bandeau, pour
 * que l'en tête de la page ne passe pas dessous. La hauteur est mesurée (et
 * suivie par ResizeObserver) : sur mobile, le texte passe sur deux lignes.
 *
 * Ce bloc ne décale que le contenu dans le flux. Une barre FIXE collée en haut
 * (MobileHeader) resterait sous le bandeau : la même hauteur est donc publiée
 * dans la variable CSS --update-banner-height, sur la racine du document, et
 * retirée au démontage. Une barre fixe du haut se place à
 * `top: var(--update-banner-height, 0px)`. Rien n'est posé sur body.
 */
export function UpdateBannerShell({ message, actionLabel, onAction, spacerTestId }: UpdateBannerShellProps) {
  const bannerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const measure = () => {
      const measured = el.offsetHeight;
      setHeight(measured);
      root.style.setProperty(BANNER_HEIGHT_VARIABLE, `${measured}px`);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VARIABLE);
    };
  }, []);

  return (
    <>
      <div aria-hidden="true" data-testid={spacerTestId} style={{ height }} />
      <div
        ref={bannerRef}
        role="alert"
        className="fixed inset-x-0 top-0 z-[100] flex flex-wrap items-center justify-center gap-3 bg-primary px-4 pb-2 text-sm text-primary-foreground shadow-md"
        style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}
      >
        <span>{message}</span>
        <Button size="sm" variant="secondary" onClick={onAction}>
          {actionLabel}
        </Button>
      </div>
    </>
  );
}
